import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { Dialog, PillView, useAction, useRueckfrage } from "../../components/ui";
import { toast } from "../../components/Toast";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { pille } from "./konsole-model";
import {
  einladungsLink, faktorText, inviteStaff, loadTeam, revokeStaffInvitation, ROLLE, rollenErklaerung, setStaffActive, setStaffName, setStaffRole,
  vergebbareRollen, type StaffRole, type StaffRow,
} from "./team-data";
import { useKonsolenRolle } from "./rolle";
import { t } from "../../lib/sprache";

/**
 * Team der Betreiber-Konsole (Vertrieb-Konsole, Inhaber 02.11.2026; 0500: Entwicklung verwaltet alle, Admin nur den Vertrieb):
 * Mitarbeiter per E-Mail einladen, Rolle wählen, deaktivieren. Der Einladungslink gilt 7 Tage und nur für die eingeladene, bestätigte Adresse; für die Konsole
 * brauchen alle einen zweiten Faktor. Jede Änderung steht im Protokoll.
 */
export const TEAM_KEY = ["platform_staff_list", "betrieb"] as const;

export function Team() {
  const q = useQuery({ queryKey: TEAM_KEY, queryFn: () => loadTeam(api) });
  const eigene = useKonsolenRolle();
  const kopf = <><title>Team · Plattform · CreatorOffice</title><h1>Team</h1></>;
  if (q.error) return <>{kopf}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <>{kopf}<Laden /></>;
  const { staff, invitations } = q.data;
  return (
    <>
      {kopf}
      <ul className="muted tk-team-intro tk-liste-schlicht">
        {(["developer", "admin", "sales"] as const).map((r) => <li key={r}><strong>{ROLLE[r]}:</strong> {rollenErklaerung(r)}</li>)}
        <li>{t("Alle brauchen einen zweiten Faktor; jede Aktion steht mit der Rolle im Protokoll.", "Everyone needs a second factor; every action is logged with the role.")}
          {eigene === "admin" ? ` ${t("Als Admin lädst du nur den Vertrieb ein und verwaltest nur ihn.", "As an admin you only invite and manage sales.")}` : ""}</li>
      </ul>
      <div className="grid tk-team-raster">
        <Einladen eigene={eigene} />
        <section className="panel stack" aria-labelledby="team-offen">
          <h2 id="team-offen">Offene Einladungen</h2>
          {invitations.length === 0 ? <p className="muted">Keine offene Einladung.</p> : (
            <ul className="tk-betrieb-liste">
              {invitations.map((i) => <OffeneEinladung key={i.id} i={i} darf={vergebbareRollen(eigene).includes(i.role)} />)}
            </ul>
          )}
        </section>
      </div>
      <section className="stack" aria-labelledby="team-liste">
        <h2 id="team-liste" className="tk-abschnitt">Mitarbeiter</h2>
        <div className="tablewrap">
          <table className="tk-team-tabelle">
            <caption className="sr-only">Mitarbeiter der Betreiber-Konsole</caption>
            <thead>
              <tr><th scope="col">Person</th><th scope="col">Rolle</th><th scope="col">Zweiter Faktor</th><th scope="col">Status</th><th scope="col">Zuletzt angemeldet</th><th scope="col"><span className="sr-only">Aktionen</span></th></tr>
            </thead>
            <tbody>{staff.map((s) => <Mitarbeiter key={s.user_id} s={s} eigene={eigene} />)}</tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function Einladen({ eigene }: { eigene: "developer" | "admin" | "sales" }) {
  const { busy, error, run } = useAction();
  const wahl = vergebbareRollen(eigene);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<StaffRole>("sales");
  const [link, setLink] = useState<string | null>(null);
  async function absenden(e: FormEvent) {
    e.preventDefault();
    const r = await run("einladen", () => inviteStaff(api, { email, role, name }), "Einladung verschickt.");
    if (r) {
      setLink(einladungsLink(r.link_path, window.location.origin));
      setEmail("");
      setName("");
    }
  }
  return (
    <section className="panel stack" aria-labelledby="team-einladen">
      <h2 id="team-einladen">Mitarbeiter einladen</h2>
      <form className="stack" onSubmit={(e) => void absenden(e)}>
        <label>E-Mail-Adresse
          <input type="email" required autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vorname@example.com" />
        </label>
        <label>Name (optional)
          <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="So erscheint die Person als Zuständige" />
        </label>
        <label>{t("Rolle", "Role")}
          <select value={role} onChange={(e) => setRole(e.target.value as StaffRole)} disabled={wahl.length < 2}>
            {wahl.map((r) => <option key={r} value={r}>{ROLLE[r]}</option>)}
          </select>
        </label>
        <small className="tk-team-rolle-satz">{rollenErklaerung(role)}</small>
        <small className="muted">Der Link gilt 7 Tage und nur für diese Adresse. Wer ihn annimmt, richtet beim ersten Öffnen der Konsole den zweiten Faktor ein.{role === "sales" ? " Der Vertrieb wird außerdem Mitglied der Demo-Konten." : ""}</small>
        <div className="row">
          <button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "einladen"}>{busy === "einladen" ? "Wird verschickt …" : "Einladung schicken"}</button>
        </div>
        {error && <p className="tk-error" role="alert">{error}</p>}
      </form>
      {link && (
        <div className="notice" role="status">
          <p>Einladungslink, falls die Mail nicht ankommt (nur an die eingeladene Person weitergeben):</p>
          <code className="tk-team-link">{link}</code>
          <div className="row">
            <button type="button" onClick={() => void navigator.clipboard?.writeText(link).then(() => toast("Link kopiert."), () => toast("Kopieren nicht möglich. Bitte den Link markieren.", "alert"))}>Link kopieren</button>
            <button type="button" onClick={() => setLink(null)}>Ausblenden</button>
          </div>
        </div>
      )}
    </section>
  );
}

function OffeneEinladung({ i, darf }: { i: { id: string; email: string; role: StaffRole; display_name: string | null; expires_at: string; status: string; invited_by_email: string | null }; darf: boolean }) {
  const { busy, run } = useAction();
  const abgelaufen = i.status === "expired";
  return (
    <li className="tk-team-einladung">
      <span><strong>{i.display_name ?? i.email}</strong>{i.display_name ? <small> · {i.email}</small> : null}<br />
        <small>{ROLLE[i.role]} · {abgelaufen ? "abgelaufen" : `gültig bis ${date(i.expires_at)}`}{i.invited_by_email ? ` · von ${i.invited_by_email}` : ""}</small></span>
      {darf && (
        <button type="button" disabled={!!busy} aria-busy={busy === "zurueck"} onClick={() => void run("zurueck", () => revokeStaffInvitation(api, i.id), "Einladung zurückgenommen.")}>
          {abgelaufen ? "Entfernen" : "Zurücknehmen"}
        </button>
      )}
    </li>
  );
}

function Mitarbeiter({ s, eigene }: { s: StaffRow; eigene: "developer" | "admin" | "sales" }) {
  const wahl = vergebbareRollen(eigene);
  // Admin verwaltet nur den Vertrieb (Rolle ändern gar nicht, Zugang nur beim Vertrieb); Entwicklung alle (wie app.staff_may_manage)
  const verwaltbar = !s.me && wahl.includes(s.role);
  const { busy, run } = useAction();
  const frage = useRueckfrage();
  const [nameOffen, setNameOffen] = useState(false);
  const [name, setName] = useState(s.display_name ?? "");
  const faktor = faktorText(s);
  async function rolle(next: StaffRole) {
    if (next === s.role) return;
    const ok = await frage.ask({
      title: `Rolle auf ${ROLLE[next]} ändern?`,
      text: `${s.display_name ?? s.email}: ${rollenErklaerung(next)}`,
      confirmLabel: "Rolle ändern",
    });
    if (ok) await run("rolle", () => setStaffRole(api, s.user_id, next), `Rolle geändert: ${ROLLE[next]}.`);
  }
  async function zugang() {
    const ok = await frage.ask(s.active
      ? { title: "Zugang deaktivieren?", text: `${s.display_name ?? s.email} kommt danach nicht mehr in die Konsole${s.role === "sales" ? " und verliert den Zugang zu den Demo-Konten" : ""}. Leads bleiben zugeordnet und lassen sich übergeben.`, confirmLabel: "Deaktivieren", danger: true }
      : { title: "Zugang wieder aktivieren?", text: `${s.display_name ?? s.email} kommt wieder in die Konsole, mit der Rolle ${ROLLE[s.role]}.`, confirmLabel: "Aktivieren" });
    if (ok) await run("zugang", () => setStaffActive(api, s.user_id, !s.active), s.active ? "Zugang deaktiviert." : "Zugang aktiviert.");
  }
  return (
    <tr>
      <td>
        <strong>{s.display_name ?? s.email}</strong>{s.me ? <small> (du)</small> : null}
        <small>{s.email}</small>
        {(verwaltbar || s.me) && <button type="button" className="tk-link-knopf" onClick={() => setNameOffen(true)}>Name ändern</button>}
      </td>
      <td>
        {!verwaltbar || wahl.length < 2 ? ROLLE[s.role] : (
          <select aria-label={`Rolle von ${s.display_name ?? s.email}`} value={s.role} disabled={!!busy || !s.active} onChange={(e) => void rolle(e.target.value as StaffRole)}>
            {wahl.map((r) => <option key={r} value={r}>{ROLLE[r]}</option>)}
          </select>
        )}
      </td>
      <td><PillView pill={pille(faktor.text, faktor.tone)} /></td>
      <td><PillView pill={s.active ? pille("aktiv", "success") : pille(`deaktiviert${s.deactivated_at ? ` seit ${date(s.deactivated_at)}` : ""}`, "neutral")} /></td>
      <td>{s.last_login_at ? `${date(s.last_login_at)} ${time(s.last_login_at)}` : "–"}</td>
      <td>
        {verwaltbar && (
          <button type="button" className={s.active ? "danger" : ""} disabled={!!busy} aria-busy={busy === "zugang"} onClick={() => void zugang()}>
            {s.active ? "Deaktivieren" : "Aktivieren"}
          </button>
        )}
        {frage.element}
        <Dialog open={nameOffen} onClose={() => setNameOffen(false)} title="Name im Team">
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void run("name", () => setStaffName(api, s.user_id, name).then(() => true), "Name gespeichert.").then((r) => { if (r) setNameOffen(false); }); }}>
            <label>Name<input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} /></label>
            <small className="muted">So erscheint die Person als Zuständige bei Leads. Leer: die E-Mail-Adresse.</small>
            <div className="row"><button type="submit" className="primary" disabled={!!busy}>Speichern</button><button type="button" onClick={() => setNameOffen(false)}>Abbrechen</button></div>
          </form>
        </Dialog>
      </td>
    </tr>
  );
}
